import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useGetOrderBalancesQuery } from '../../../../api/endpoints/parts'; 
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../utils/filterUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { FilterMatchMode } from 'primereact/api';

const useOrderBalanceCollection = () => {
  const { th } = useLanguage();
  const toast = useRef(null);

  const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
  const [apiFilters, setApiFilters] = useState({});
  const [filters, setFilters] = useState({});

  const filterFields = useMemo(
    () => [
      { field: 'dealerName',        headerName: th('parts.dealerName'),        type: 'text' },
      { field: 'shippingAddressId', headerName: th('parts.shippingAddressId'), type: 'text' },
      { field: 'orderNo',           headerName: th('parts.orderNo'),           type: 'text' },
    ],
    [th]
  );

  const filterTypeMap = useMemo(
    () => ({
      dealerName:        { type: 'text', matchMode: FilterMatchMode.CONTAINS },
      shippingAddressId: { type: 'text', matchMode: FilterMatchMode.CONTAINS },
      orderNo:           { type: 'text', matchMode: FilterMatchMode.CONTAINS },
    }),
    []
  );

  const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
  const initialFilters = useMemo(
    () => createInitialFilters(filterFieldNames, filterTypeMap),
    [filterFieldNames, filterTypeMap]
  );

  useEffect(() => setFilters(initialFilters), [initialFilters]);

  const {
    data: orderBalanceData,
    isLoading
  } = useGetOrderBalancesQuery({
    ActiveOnly: 1,
    pageNumber: lazyParams.page,
    pageSize: lazyParams.rows,
    ...apiFilters
  });

  const handleApplyFilters = useCallback(
    (updatedFilters) => {
      const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
      setFilters(converted);

      const newApiFilters = {};
      Object.entries(updatedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          let processedValue = value;
          if (key === 'activeOnly') {
            processedValue = value ? 1 : 0;
          }
          // API'ye PascalCase gönder: DealerName / ShippingAddressId / OrderNo
          const pascal = key.charAt(0).toUpperCase() + key.slice(1);
          newApiFilters[pascal] = processedValue;
        }
      });
      setApiFilters(newApiFilters);
    },
    [filters, filterTypeMap]
  );

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setApiFilters({});
  };

  return {
    toast,
    filterFields,
    customFilterOptions: {},
    handleSearch: () => {},
    handleApplyFilters,
    handleResetFilters,
    tableProps: {
      data: orderBalanceData?.items || [],
      totalRecords: orderBalanceData?.totalCount || 0,
      isLoading,
      onPage: (e) => setLazyParams({ page: e.page + 1, rows: e.rows }),
      lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
      resetPagination: false
    }
  };
};

export default useOrderBalanceCollection;
